import { useTranslation } from '@kgc/i18n';
import { toast } from '@kgc/ui';
import { useEffect, useRef } from 'react';
import type { FlowTab, WorkflowExecution } from '../types';

export function useExecutionToasts(
  executions: WorkflowExecution[],
  onNavigate?: ((tab: FlowTab) => void) | undefined
) {
  const { t } = useTranslation();
  const prevRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    const prevStatuses = prevRef.current;

    // Skip first render (no previous state to compare)
    if (prevStatuses.size === 0) {
      for (const exec of executions) {
        prevStatuses.set(exec.id, exec.status);
      }
      return;
    }

    for (const exec of executions) {
      const prevStatus = prevStatuses.get(exec.id);
      if (!prevStatus || prevStatus === exec.status) continue;

      if (exec.status === 'completed') {
        toast.success(t('integration.flow.toast.completed', { name: exec.workflowName }), {
          duration: 5000,
          action: { label: t('common.details'), onClick: () => onNavigate?.('history') },
        });
      } else if (exec.status === 'failed') {
        toast.error(t('integration.flow.toast.failed', { name: exec.workflowName }), {
          duration: 5000,
          action: { label: t('common.details'), onClick: () => onNavigate?.('history') },
        });
      }
    }

    // Update prev state
    const next = new Map<string, string>();
    for (const exec of executions) {
      next.set(exec.id, exec.status);
    }
    prevRef.current = next;
  }, [executions, t, onNavigate]);
}
